import { useCallback, useState } from 'react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import {
  SelectionResponse,
  SubjectSelection,
  SubjectWithCandidates,
} from '@/types/subject-candidate';
import { toError } from '@/utils/errors';
import { getCachedCandidates, setCachedCandidates } from './subjectCandidatesCache';
import { UNCHANGED, usePoll, withEtag } from './usePoll';

const POLL_INTERVAL = 5000;
const MAX_POLL_DURATION = 600000; // 10 minutes

interface UseSubjectCandidatesReturn {
  subjects: SubjectWithCandidates[];
  isLoading: boolean;
  isSubmitting: boolean;
  error: string | null;
  /** True when results came from the in-memory cache (a prior fetch this session). */
  isFromCache: boolean;
  /** Manual refetch — always a real read. */
  refetch: () => Promise<void>;
  /**
   * Submit a user's decision for a subject. Returns the server response so the
   * caller can react to `next_action` (route back, show failure, etc.).
   */
  submit: (subjectId: string, selection: SubjectSelection) => Promise<SelectionResponse>;
}

interface CandidatesSnapshot {
  /** The report the rows belong to: report A's rows must never show under
   * report B while B's first fetch is in flight. */
  reportId: string;
  rows: SubjectWithCandidates[];
}

const EMPTY_SUBJECTS: SubjectWithCandidates[] = [];

const hasUnresolved = (rows: SubjectWithCandidates[]) => rows.some((s) => !s.is_resolved);

/**
 * Manages candidate selection state for a self-served Report sitting
 * at ``lifecycle_stage = triage`` with unresolved candidates.
 *
 * Fetches GET /reports/{id}/candidates and polls every 5s while any subject
 * has an open iteration (unresolved or mid re-identification), for at most
 * ten minutes per identification round. Submission returns the server's
 * `SelectionResponse` so the caller can route/toast on `next_action`.
 */
export function useSubjectCandidates(
  reportId: string | null | undefined
): UseSubjectCandidatesReturn {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Bumped when a submission re-runs identification: restarts the poll
  // (and its ten-minute clock) for the new round.
  const [round, setRound] = useState(0);

  const poll = usePoll<CandidatesSnapshot>({
    enabled: Boolean(reportId),
    interval: (snapshot) => (snapshot && hasUnresolved(snapshot.rows) ? POLL_INTERVAL : null),
    maxDurationMs: MAX_POLL_DURATION,
    refetchDeps: [reportId, round],
    queryFn: async ({ signal, etag, manual }) => {
      if (!reportId) {
        throw new Error('reportId is required');
      }
      // Scheduled ticks poll on the stored token (a 304 is the steady
      // state while identification runs); manual reads are always full.
      const result = await vnextApiClient.getReportCandidates(reportId, {
        signal,
        etag: manual ? null : etag,
      });
      if (result === 'not-modified') {
        return UNCHANGED;
      }
      // Write-through so the next mount paints the freshest result instantly.
      setCachedCandidates(reportId, result.data);
      return withEtag({ reportId, rows: result.data }, result.etag);
    },
    onData: () => setSubmitError(null),
    onError: (err) => Sentry.captureException(toError(err)),
  });

  // Seed from the process-lifetime cache so a panel re-mounted after a
  // navigation paints the last-known candidates instantly instead of
  // flashing a loader (which read as "identification is running again").
  const cached = getCachedCandidates(reportId);
  const current = reportId && poll.data?.reportId === reportId ? poll.data.rows : undefined;
  const subjects = current ?? cached ?? EMPTY_SUBJECTS;
  const isFromCache = current === undefined && cached !== undefined;

  const { refetch: pollRefetch } = poll;
  const refetch = useCallback(
    () => (reportId ? pollRefetch() : Promise.resolve()),
    [reportId, pollRefetch]
  );

  const submit = useCallback(
    async (subjectId: string, selection: SubjectSelection): Promise<SelectionResponse> => {
      if (!reportId) {
        throw new Error('No report ID — cannot submit selection');
      }
      setIsSubmitting(true);
      setSubmitError(null);
      try {
        const response = await vnextApiClient.submitSubjectSelection(
          reportId,
          subjectId,
          selection
        );
        // Re-read so the UI reflects server-side state (resolved/iteration
        // bumps). A re-run of identification is a fresh round: restart the
        // poll clock rather than continuing the old one.
        if (response.next_action === 'triggered:subject_identification') {
          setRound((r) => r + 1);
        } else {
          await pollRefetch();
        }
        return response;
      } catch (err) {
        const e = toError(err, 'Failed to submit selection');
        setSubmitError(e.message);
        Sentry.captureException(e);
        throw err;
      } finally {
        setIsSubmitting(false);
      }
    },
    [reportId, pollRefetch]
  );

  const fetchError =
    poll.error === undefined ? null : toError(poll.error, 'Failed to fetch candidates').message;

  return {
    subjects,
    // Cached results stay on screen while the silent revalidation runs.
    isLoading: poll.isLoading && !isFromCache,
    isSubmitting,
    error: submitError ?? fetchError,
    isFromCache,
    refetch,
    submit,
  };
}
